# Deps
import Vue from 'vue'
import _throttle from 'lodash/throttle'
import _debounce from 'lodash/debounce'
import memoize from 'lodash/memoize'
import atob from 'atob-lite'
import { shopifyRootPaths } from 'library/constants'
import Cookies from 'js-cookie'

# Re-export some helpers from bkwld/cloak
import { wait, nonEmpty } from '@bkwld/cloak/services/helpers'
export { wait, nonEmpty }

# Throttle with order of arguments reverse
export throttle = (delay, cb) -> _throttle cb, delay

# Debounce with order of arguments reverse
export debounce = (delay, cb) -> _debounce cb, delay

# Mount a component on the body, like a modal, and return the mounted component
# instance. The "component" argument should be a Vue component instance, like
# returned from importing a single file component.
export mountOnBody = (component, options = {}) ->
	appendMount document.body, component, options

export appendMount = (element, component, options = {}) ->
	mountInside element, component, options, true


# Mount a component within an element
mountInside = (
	element,
	component,
	options = {},
	append = true) -> new Promise (resolve) ->

	# Mount the component
	attachMethod = if append then 'appendChild' else 'prepend'
	mount = ->

		# Set default options
		unless options.parent then options.parent = window.$nuxt?.$root

		# Instantiate and mount
		vm = new (Vue.extend component)(options)
		vm.$mount()
		element[attachMethod](vm.$el)
		resolve vm

	# Wait for $nuxt befre mounting
	if window.$nuxt then mount() else wait 50, mount

# Get the id from a Shopify gid:// style id.  This strips everything but the
# last part of the string.  So gid://shopify/ProductVariant/34641879105581
# becomes 34641879105581
# https://regex101.com/r/3FIplL/3
export getShopifyId = (id) ->
	return unless id

	# Already a simple ID
	return id if String(id).match /^\d+$/

	# De-base64. This should only be required when migrating cart ids that were
	# stored in a cookie, AKA client-side pre Storefront API version 2022-04.
	id = atob(id) unless id.match /^gid:\/\//

	# Return the ID
	matches = id.match /\/([^\/?]+)(?:\?.+)?$/
	return matches[1] if matches

# Determine if the URL is a Shopify URL
isShopifyRegex = new RegExp "^\/(#{shopifyRootPaths.join('|')})", 'i'
export isShopifyUrl = (url) ->

	# example url: /account or /
	return isShopifyRegex.test url

# Determine if the URL is a Nuxt URL
export isNuxtUrl = (url) -> not isShopifyUrl url

# Make a Shopify URL
export makeShopifyUrl = (url) ->
	urlObj = new URL url, process.env.SHOPIFY_URL
	urlObj.host = getUrlHost process.env.SHOPIFY_URL
	return urlObj.toString()

# Get the Nuxt app host
export getUrlHost = memoize (url) -> (new URL url).host

# Make a Nuxt URL
export makeNuxtUrl = (url) ->
	urlObj = new URL url, getNuxtAppUrl()
	urlObj.host = getUrlHost getNuxtAppUrl()
	return urlObj.toString()

# Get the host for the nuxt app, which may vary based on the current URL
# This is only used when generating links back to Nuxt from Shopify.
export getNuxtAppUrl = memoize -> switch
	when window?.location.hostname.match /papaandbarkleyhemp\.com$/
		process.env.NUXT_APP_HEMP_URL ||
		process.env.NUXT_APP_URL ||
		process.env.URL
	else process.env.NUXT_APP_URL || process.env.URL

# Make a URL for the CBD site
export makeCBDUrl = (url) ->
	urlObj = new URL url, process.env.CBD_SITE_URL
	urlObj.host = getUrlHost process.env.CBD_SITE_URL
	return urlObj.toString()

# Format a string or number like money
export formatMoney = (val) ->
	val = val.amount if val?.amount? # From Shopify MoneyV2
	twoDecimals val,
		style: 'currency'
		currency: 'USD'

# Format money for a product, which may be free
export productCost = (val) ->
	val = val.amount if val?.amount? # From Shopify MoneyV2
	if parseFloat(val) == 0
	then 'Free'
	else formatMoney val

# Add two decimal places
export twoDecimals = (val, options) ->
	locale = navigator?.language || 'en-US'
	parseFloat(val).toLocaleString locale, {
		minimumFractionDigits: 2
		maximumFractionDigits: 2
		...options
	}

# Create a script tag and watch for it to load
export loadScript = memoize (src, {
	defer = false
	async = false
} = {}) -> return new Promise (resolve, reject) ->
	script = document.createElement('script')
	script.onload = resolve
	script.onerror = reject
	script.src = src
	script.defer = true if defer
	script.async = true if async
	document.head.appendChild script

# Helpers for interacting with cookies that get shared across domains
export cookie = new class SharedCookie

	# Make the env-specifc key suffic
	constructor: ->
		@suffix = if (env = process.env.APP_ENV) == 'prod'
		then '' else "-#{env}"

	# Set a cookie that can be shared across subdomains
	set: (key, value, options = {}) ->
		Cookies.set key + @suffix, value, {

			# Don't try to share between domains when on a deploy preview
			domain: do ->
				if (apexDomain = currentApexDomain()) == 'netlify.app'
				then undefined else apexDomain

			# Don't expect SSL locally
			secure: location.hostname not in ['localhost']

			# Don't send to any 3rd party sites
			sameSite: 'strict'

			# Allow overriding
			...options
		}

	# Get a cookie
	get: (key) -> Cookies.get key + @suffix

	# Remove a cookie
	remove: (key) -> Cookies.remove key + @suffix, domain: currentApexDomain()

# Get the current apex (root) domain
export currentApexDomain = ->
	location.hostname.match(/(?:^|\.)((?:[^.]+\.)?[^.]+)$/)[1]
